// src/components/geofencing/GeofenceManagement.js
import React, { useState, useMemo, useCallback } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Card, Table, Badge, Button, Modal, FormField, Input, Select, SearchInput, ConfirmDialog, ResponsiveGrid, Toggle } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';
import LocationAutocomplete from '../documents/LocationAutocomplete';
import GeofenceMap from './GeofenceMap';

function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function pointInPolygon(lat, lng, polygon) {
  const poly = polygon.map(p => Array.isArray(p) ? p : [p.lat, p.lng]);
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [latI, lngI] = poly[i];
    const [latJ, lngJ] = poly[j];
    const intersect = ((lngI > lng) !== (lngJ > lng)) &&
      (lat < (latJ - latI) * (lng - lngI) / (lngJ - lngI) + latI);
    if (intersect) inside = !inside;
  }
  return inside;
}

function formatRadius(m) {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`;
}

const emptyForm = () => ({
  name: '', truckId: '', documentId: '',
  shape: 'circle',
  centerLat: null, centerLng: null, address: '',
  radiusMeters: 1000,
  points: [],
  color: '#3b82f6',
});

const SHAPE_COLORS = ['#3b82f6', '#f97316', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4'];

export default function GeofenceManagement() {
  const { geofences, addGeofence, updateGeofence, deleteGeofence, trucks, documents } = useApp();
  const { isMobile } = useResponsive();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editGeofence, setEditGeofence] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const filtered = geofences.filter(g =>
    g.name?.toLowerCase().includes(search.toLowerCase()) ||
    g.address?.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setEditGeofence(null);
    setForm({ ...emptyForm(), color: SHAPE_COLORS[geofences.length % SHAPE_COLORS.length] });
    setModalOpen(true);
  };

  const openEdit = (gf) => {
    setEditGeofence(gf);
    setForm({
      name: gf.name, truckId: gf.truckId, documentId: gf.documentId || '',
      shape: gf.shape || 'circle',
      centerLat: gf.centerLat, centerLng: gf.centerLng, address: gf.address || '',
      radiusMeters: gf.radiusMeters || 1000,
      points: gf.points || [],
      color: gf.color || '#3b82f6',
    });
    setModalOpen(true);
  };

  const handleLocationSelect = (loc) => {
    setForm(f => ({ ...f, centerLat: loc.lat, centerLng: loc.lng, address: loc.address }));
  };

  const handleMapCenterChange = useCallback((pos) => {
    setForm(f => ({ ...f, centerLat: pos.lat, centerLng: pos.lng }));
  }, []);

  const handleAddPoint = useCallback((pos) => {
    setForm(f => ({ ...f, points: [...f.points, pos] }));
  }, []);

  const handleUndoPoint = () => setForm(f => ({ ...f, points: f.points.slice(0, -1) }));
  const handleClearPoints = () => setForm(f => ({ ...f, points: [] }));

  const handleShapeChange = (shape) => {
    setForm(f => ({ ...f, shape }));
  };

  const isValid = form.name && form.truckId && (
    form.shape === 'polygon' ? form.points.length >= 3 : !!form.centerLat
  );

  const handleSave = () => {
    if (!isValid) return;
    if (editGeofence) updateGeofence(editGeofence.id, form);
    else addGeofence(form);
    setModalOpen(false);
  };

  // Live inside/outside status per geofence, based on each truck's current stored location
  const statusFor = useMemo(() => {
    const map = {};
    geofences.forEach(gf => {
      const truck = trucks.find(t => t.id === gf.truckId);
      if (!truck?.location?.lat) { map[gf.id] = 'unknown'; return; }
      if (gf.shape === 'polygon' && gf.points?.length >= 3) {
        const polygon = gf.points.map(p => [p.lat, p.lng]);
        map[gf.id] = pointInPolygon(truck.location.lat, truck.location.lng, polygon) ? 'inside' : 'outside';
      } else {
        const dist = distanceMeters(gf.centerLat, gf.centerLng, truck.location.lat, truck.location.lng);
        map[gf.id] = dist > gf.radiusMeters ? 'outside' : 'inside';
      }
    });
    return map;
  }, [geofences, trucks]);

  const insideCount = Object.values(statusFor).filter(s => s === 'inside').length;
  const outsideCount = Object.values(statusFor).filter(s => s === 'outside').length;

  const selectedTruck = trucks.find(t => t.id === form.truckId);
  const truckDocs = documents.filter(d => d.truckId === form.truckId);

  const columns = [
    {
      key: 'name', label: 'Geofence',
      render: (val, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: (row.color || '#3b82f6') + '22', color: row.color || '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>
            {row.shape === 'polygon' ? '⬡' : '📍'}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--textPrimary)', fontFamily: 'Syne, sans-serif' }}>{val}</div>
            <div style={{ fontSize: 11, color: 'var(--textFaint)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.shape === 'polygon' ? `Custom shape` : row.address}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'truckId', label: 'Assigned Truck',
      render: v => {
        const truck = trucks.find(t => t.id === v);
        return truck ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: truck.color }} />
            <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{truck.plateNumber}</span>
          </div>
        ) : <span style={{ color: 'var(--textFaint)' }}>—</span>;
      }
    },
    {
      key: 'radiusMeters', label: 'Size',
      render: (v, row) => (
        <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>
          {row.shape === 'polygon' ? `${row.points?.length || 0} points` : formatRadius(v)}
        </span>
      )
    },
    {
      key: 'status', label: 'Status',
      render: (v, row) => {
        const s = statusFor[row.id];
        if (s === 'inside') return <Badge color="#10b981">✓ Inside</Badge>;
        if (s === 'outside') return <Badge color="#ef4444">⚠ Outside</Badge>;
        return <Badge color="#6b7280">Unknown</Badge>;
      }
    },
    {
      key: 'active', label: 'Active',
      render: (v, row) => <Toggle checked={v} onChange={checked => updateGeofence(row.id, { active: checked })} />
    },
    {
      key: 'id', label: '',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); setConfirmDelete(row); }}>Del</Button>
        </div>
      )
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, alignItems: isMobile ? 'stretch' : 'center', marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search geofences..." />
          </div>
          <Button onClick={openAdd} icon="＋">Create Geofence</Button>
        </div>

        <ResponsiveGrid columns={3} tabletColumns={3} mobileColumns={1} gap={12} style={{ marginBottom: 20 }}>
          <div style={{ background: 'var(--bgInput)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 32, color: 'var(--textPrimary)' }}>{geofences.length}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: 4 }}>Total Geofences</div>
          </div>
          <div style={{ background: 'var(--bgInput)', border: '1px solid rgba(16,185,129,0.22)', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 32, color: '#10b981' }}>{insideCount}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: 4 }}>Inside Zone</div>
          </div>
          <div style={{ background: 'var(--bgInput)', border: '1px solid rgba(239,68,68,0.22)', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 32, color: '#ef4444' }}>{outsideCount}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: 4 }}>Outside Zone</div>
          </div>
        </ResponsiveGrid>

        <Table columns={columns} data={filtered} emptyMessage="No geofences created yet" />
      </Card>

      {/* Add / Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editGeofence ? 'Edit Geofence' : 'Create Geofence'} width={640}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
            <FormField label="Geofence Name" required>
              <Input value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="e.g. Manila Delivery Zone" required />
            </FormField>
            <FormField label="Assign to Truck" required>
              <Select value={form.truckId} onChange={v => setForm(f => ({ ...f, truckId: v, documentId: '' }))} placeholder="Select truck" options={trucks.map(t => ({ value: t.id, label: `${t.plateNumber} — ${t.model}` }))} />
            </FormField>
          </div>

          {form.truckId && truckDocs.length > 0 && (
            <FormField label="Link to Shipment" hint="Optional — associates this geofence with a specific delivery">
              <Select value={form.documentId} onChange={v => setForm(f => ({ ...f, documentId: v }))} placeholder="No specific shipment" options={truckDocs.map(d => ({ value: d.id, label: `${d.docNumber} — ${d.destination || 'No destination'}` }))} />
            </FormField>
          )}

          {/* Shape selector */}
          <FormField label="Zone Shape">
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => handleShapeChange('circle')}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 9, cursor: 'pointer',
                  background: form.shape === 'circle' ? 'var(--bgActive)' : 'var(--bgInput)',
                  border: `1.5px solid ${form.shape === 'circle' ? 'rgba(249,115,22,0.4)' : 'var(--borderInput)'}`,
                  color: form.shape === 'circle' ? 'var(--textPrimary)' : 'var(--textMuted)',
                  fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                ⭕ Circle <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>(center + radius)</span>
              </button>
              <button
                type="button"
                onClick={() => handleShapeChange('polygon')}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 9, cursor: 'pointer',
                  background: form.shape === 'polygon' ? 'var(--bgActive)' : 'var(--bgInput)',
                  border: `1.5px solid ${form.shape === 'polygon' ? 'rgba(249,115,22,0.4)' : 'var(--borderInput)'}`,
                  color: form.shape === 'polygon' ? 'var(--textPrimary)' : 'var(--textMuted)',
                  fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                ⬡ Custom Shape <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>(click points)</span>
              </button>
            </div>
          </FormField>

          {form.shape === 'circle' ? (
            <>
              <FormField label="Center Location" hint="Search an address, or click/drag directly on the map below">
                <LocationAutocomplete
                  value={form.centerLat ? { address: form.address, lat: form.centerLat } : { address: form.address }}
                  onSelect={handleLocationSelect}
                  placeholder="e.g. Ayala Center, Makati"
                  icon="📍"
                />
              </FormField>

              <FormField label={`Radius — ${formatRadius(form.radiusMeters)}`}>
                <input
                  type="range" min="200" max="20000" step="100"
                  value={form.radiusMeters}
                  onChange={e => setForm(f => ({ ...f, radiusMeters: parseInt(e.target.value) }))}
                  style={{ width: '100%', accentColor: '#f97316' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--textFaint)', marginTop: 2 }}>
                  <span>200 m</span>
                  <span>20 km</span>
                </div>
              </FormField>
            </>
          ) : (
            <FormField label="Zone Outline" hint="Click on the map to place each corner point, in order around the shape">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Badge color={form.points.length >= 3 ? '#10b981' : '#f59e0b'}>
                  {form.points.length} point{form.points.length !== 1 ? 's' : ''} {form.points.length < 3 && '(need 3+)'}
                </Badge>
                <Button size="sm" variant="ghost" onClick={handleUndoPoint} disabled={form.points.length === 0}>↩ Undo Last</Button>
                <Button size="sm" variant="ghost" onClick={handleClearPoints} disabled={form.points.length === 0}>🗑 Clear All</Button>
              </div>
            </FormField>
          )}

          <GeofenceMap
            key={editGeofence?.id || 'new'}
            shape={form.shape}
            center={form.centerLat ? { lat: form.centerLat, lng: form.centerLng } : null}
            radiusMeters={form.radiusMeters}
            onCenterChange={handleMapCenterChange}
            points={form.points}
            onAddPoint={handleAddPoint}
            color={form.color}
            truckPosition={selectedTruck?.location?.lat ? selectedTruck.location : null}
            height={280}
          />

          {!isValid && (form.name || form.truckId) && (
            <div style={{ fontSize: 11, color: '#f59e0b', textAlign: 'center' }}>
              {form.shape === 'polygon' ? '⚠ Add at least 3 points on the map before saving' : '⚠ Set a center location above before saving'}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!isValid}>
            {editGeofence ? 'Save Changes' : 'Create Geofence'}
          </Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deleteGeofence(confirmDelete.id)}
        title="Delete Geofence"
        message={`Delete "${confirmDelete?.name}"? Alerts will no longer fire for this zone.`}
      />
    </div>
  );
}
